// Copyright (c) Ping. All rights reserved.
//
// invite-flow Wave-3B — thin host shim. Web's calls home is now
// rendered by the shared `<CallHistoryList>` (in
// `packages/ui/src/calls/`). This file's only job is to map the
// web `CallHistoryEntry` records (which carry the full
// `DirectoryUser` from `core/network/users.ts`) into the shared
// `CallHistoryEntry` shape that the shared UI consumes.
//
// All WebRTC orchestration (LiveKit room mount, `getUserMedia`,
// `RTCPeerConnection`, etc.) continues to live in
// `apps/web/src/calls/`. This shim only renders the call-history
// pane and dispatches "start call" events back to its parent.

import { useMemo } from 'react';

import { type CallHistoryEntry as CallHistoryEntryUi, CallHistoryList } from '@ping/ui';

import type { CallHistoryEntry, CallKind } from '@core/network/calls';

import { useCallHistory } from './useCallHistory';

interface CallHistoryHomeProps {
  onStartCall: (userId: string, kind: CallKind) => void;
}

export function CallHistoryHome({ onStartCall }: CallHistoryHomeProps) {
  const { calls, isLoading } = useCallHistory();

  const adaptedCalls = useMemo<readonly CallHistoryEntryUi[]>(() => calls.map(toUiEntry), [calls]);

  return (
    <CallHistoryList
      calls={adaptedCalls}
      isLoading={isLoading}
      onStartCall={(userId, kind) => onStartCall(userId, kind as CallKind)}
    />
  );
}

function toUiEntry(entry: CallHistoryEntry): CallHistoryEntryUi {
  return {
    id: entry.id,
    user: {
      id: entry.user.id,
      displayName: entry.user.displayName,
      ...(entry.user.avatarUri !== undefined ? { avatarUri: entry.user.avatarUri } : {}),
      ...(entry.user.role !== undefined ? { role: entry.user.role } : {}),
      ...(entry.user.company !== undefined ? { company: entry.user.company } : {}),
      ...(entry.user.presence !== undefined ? { presence: entry.user.presence } : {}),
    },
    direction: entry.direction,
    kind: entry.kind,
    timestamp: entry.timestamp,
    ...(entry.durationSec !== undefined ? { durationSec: entry.durationSec } : {}),
    ...(entry.participants !== undefined ? { participants: entry.participants } : {}),
  };
}
